import React, { useEffect, useMemo, useState } from 'react';
import {
  PlusCircleFilled,
  SettingOutlined,
  BookOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Layout, Menu, Spin, Tooltip } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './AppSideMenu.less';
import { AppRoutes } from '../../constants/route';
import { ReactComponent as AdminAgentChat } from '../../assets/admin-agent-chat.svg';
import { ReactComponent as ChangeAgent } from '../../assets/change-agent.svg';
import { ReactComponent as IconList } from '../../assets/icon-list.svg';
import { useUserAccess } from '../../hooks/useUserAccess';
import { useSidebarMenuItem } from '../../hooks/useSidebarMenuItem';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { useAgentDispatch, useAgentSelector } from '../../hooks/useAgentStore';
import { setNewThreadId } from '../../app/Slices/adminAgentSlice';
import { setRevokeAPICall } from '../../services/copilotThread.service';
import { ChangeRequestListingScope } from '../../enums/changeRequest';
import ChangeRequestScopeSelect from './ChangeRequestScopeSelect';
import { ChangeRequestGroup, Result } from '../../interfaces/changeRequest';
import { setChangeRequestsListingScope } from '../../app/Slices/ChangeRequestItemsSlice';
import { fetchPromptLibrary } from '../../app/Slices/promptLibrarySlice';
import classNames from 'classnames';
import { MenuItemType } from 'antd/es/menu/interface';
import { Thread } from '../../interfaces/copilotChat';
const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];
type MenuClickEvent = Parameters<NonNullable<MenuProps['onClick']>>[0];

type CommonMenuItemOptions = {
  key: string;
  path: string;
  label: string;
  className?: string;
  icon?: React.ReactNode;
  onClick?: (e: MenuClickEvent) => void;
};

function getItem(
  label: React.ReactNode | string,
  key: React.Key,
  icon?: React.ReactNode,
  className = '',
  popupClassName = 'app-sidebar-submenu-popup',
  children?: MenuItem[]
): MenuItem {
  return {
    key,
    icon,
    label,
    children,
    className,
    popupClassName,
  } as MenuItem;
}

const AppSideMenu: React.FC<{
  collapsed: boolean;
}> = ({ collapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAdminAccess, isTeamUser } = useUserAccess();
  const {
    isThreadsLoading,
    navigateToNewChat,
    navigateToNewChangeAgent,
    editingThreadId,
    renderThreadItem,
    renderChangeRequestItem,
    getSelectedItemKey,
    initializeAdminAgent,
    initializeChangeRequests,
    handleNavigation,
  } = useSidebarMenuItem();

  const agentDispatch = useAgentDispatch();
  const { groupedThreads } = useAgentSelector(
    (state) => state.adminAgent || { groupedThreads: [] }
  );
  const { loading: changeRequestsLoading } = useAgentSelector(
    (state) => state.changeRequestItems || { loading: false }
  );
  const { myChangeRequests, allChangeRequests } = useAgentSelector(
    (state) =>
      state.changeRequestItems || {
        myChangeRequests: [],
        allChangeRequests: [],
      }
  );
  const changeRequestScope = useAgentSelector(
    (state) => state.changeRequestItems?.listingScope || 'MY'
  );

  const [selectedItemKey, setSelectedItemKey] = useState<string>('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [stateOpenKeys, setStateOpenKeys] = useState<string[]>([]);
  const { adminAgentEnabled, changeAgentEnabled } = useSelector(
    (state: RootState) => state.appFeature
  );
  const { org } = useSelector((state: RootState) => state.organization);

  const changeRequestState = useMemo(() => {
    return changeRequestScope === ChangeRequestListingScope.MY
      ? myChangeRequests
      : allChangeRequests;
  }, [changeRequestScope, myChangeRequests, allChangeRequests]);

  useEffect(() => {
    if (org?.id && org?.organization_id && org?.user_id) {
      console.debug('AppSideMenu: Org is selected, initializing data', {
        orgId: org.id,
        organizationId: org.organization_id,
        userId: org.user_id,
      });
      if (adminAgentEnabled) {
        initializeAdminAgent();
        agentDispatch(fetchPromptLibrary());
      }
      if (changeAgentEnabled) {
        initializeChangeRequests();
      }
    } else {
      console.debug(
        'AppSideMenu: No org selected or missing required fields, skipping initialization',
        { org }
      );
    }
    return () => {
      setRevokeAPICall(true);
    };
  }, [org?.id, org?.organization_id, org?.user_id]);

  useEffect(() => {
    setMenuItemState();
  }, [location.pathname]);

  useEffect(() => {
    if (selectedItemKey) {
      setSelectedKeys([selectedItemKey]);
    }
  }, [selectedItemKey]);

  useEffect(() => {
    if (collapsed) {
      setStateOpenKeys([]);
    } else {
      setMenuItemState();
    }
  }, [collapsed]);

  const setMenuItemState = () => {
    const pathName = location.pathname.split('/').filter(Boolean);
    if (pathName.length >= 1 && pathName[0]) {
      const tabKey = '/' + pathName[0];
      !collapsed && onOpenChange && onOpenChange([tabKey], location.pathname);
      const key = getSelectedItemKey(location.pathname);
      setSelectedItemKey(key);
      setSelectedKeys([tabKey, key]);
    }
  };

  const getLoadingMenuItem = (key: string): MenuProps['items'] => [
    {
      key,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '8px',
          }}
        >
          <Spin size="small" />
        </div>
      ),
      disabled: true,
    },
  ];

  const createMenuItem = ({
    key,
    path,
    label,
    className,
    icon,
    onClick,
  }: CommonMenuItemOptions): Required<MenuProps>['items'][number] => ({
    key,
    className: classNames(className, 'app-menu-item'),
    icon,
    label: (
      <Tooltip title={label}>
        <Link to={path}>
          <span>{label}</span>
        </Link>
      </Tooltip>
    ),
    onClick,
  });

  const createItemGroup = ({
    key,
    label,
    items,
    getItemProps,
  }: {
    key: string;
    label: string;
    items: (Thread | Result)[];
    getItemProps: (item: Thread | Result) => Omit<MenuItemType, 'key'> & {
      key?: string;
      id?: string;
      active?: boolean;
    };
  }) => {
    return {
      key: key,
      label: label,
      className: 'app-menu-group',
      type: 'group' as const,
      children: items.map((item) => {
        const itemProps = getItemProps(item) || {};
        const { active = false, ...restItemProps } = itemProps;
        return {
          key: itemProps.key || item.id,
          className: classNames(
            restItemProps.className,
            'app-menu-group-item',
            active && 'active'
          ),
          ...restItemProps,
          label: (
            <div
              className="app-menu-item-wrapper"
              data-app-menu-id={item.id || itemProps.key}
            >
              {restItemProps.label}
            </div>
          ),
        };
      }),
    };
  };

  const getAdminAgentMenuItems = (): MenuProps['items'] => {
    if (!adminAgentEnabled) return [];
    if (isThreadsLoading) {
      return getLoadingMenuItem('loading');
    }

    return [
      createMenuItem({
        key: 'new-chat',
        path: AppRoutes.adminAgent.path,
        label: 'New Chat',
        className: 'new-chat',
        icon: <PlusCircleFilled />,
        onClick: (e) => {
          if (
            e.domEvent.metaKey ||
            e.domEvent.ctrlKey ||
            e.domEvent.shiftKey ||
            e.domEvent.altKey
          ) {
            return;
          }

          e.domEvent.preventDefault();
          handleNavigation(navigateToNewChat);
        },
      }),
      createMenuItem({
        key: 'prompt-library',
        path: `${AppRoutes.adminAgent.path}${AppRoutes.promptLibrary.path}`,
        label: 'Prompt Library',
        className: 'prompt-library',
        icon: <BookOutlined />,
        onClick: (e) => {
          if (
            e.domEvent.metaKey ||
            e.domEvent.ctrlKey ||
            e.domEvent.shiftKey ||
            e.domEvent.altKey
          ) {
            return;
          }

          handleNavigation(() =>
            navigate(
              `${AppRoutes.adminAgent.path}${AppRoutes.promptLibrary.path}`
            )
          );
        },
      }),
      ...groupedThreads.map((group) =>
        createItemGroup({
          key: group.key,
          label: group.label,
          items: group.threads,
          getItemProps: (_thread) => {
            const thread = _thread as Thread;
            return {
              active: editingThreadId === thread.id,
              label: renderThreadItem(thread),
            };
          },
        })
      ),
    ];
  };

  const getChangeAgentMenuItems = (): MenuProps['items'] => {
    if (!changeAgentEnabled) return [];
    if (changeRequestsLoading) {
      return getLoadingMenuItem('loading-change-agent');
    }

    const items: MenuProps['items'] = [];
    items.push(
      createMenuItem({
        key: 'new-change-request',
        path: `${AppRoutes.changeRequests.path}/new`,
        label: 'New Change Request',
        icon: <PlusCircleFilled />,
        className: 'new-change-request',
        onClick: (e) => {
          if (
            e.domEvent.metaKey ||
            e.domEvent.ctrlKey ||
            e.domEvent.shiftKey ||
            e.domEvent.altKey
          ) {
            return;
          }

          e.domEvent.preventDefault();
          handleNavigation(() =>
            navigate(`${AppRoutes.changeRequests.path}/new`)
          );
        },
      })
    );

    const iconListCrs = (
      <IconList
        // indent the icon right for optical alignment with the icon for new CR
        style={{
          marginLeft: '1px',
          paddingLeft: '1px',
          marginRight: '1px',
          marginTop: '-1px',
        }}
        onClick={() => {
          navigate(AppRoutes.changeRequests.path);
        }}
      />
    );

    if (isTeamUser) {
      items.push({
        key: 'cr-scope-select',
        className: 'app-menu-item cr-scope-select',
        icon: iconListCrs,
        label: (
          <div data-app-menu-id="cr-scope-select">
            <ChangeRequestScopeSelect
              size="small"
              scope={changeRequestScope}
              onChange={(newScope) => {
                agentDispatch(setChangeRequestsListingScope(newScope));
              }}
              onSelect={() => {
                navigate(AppRoutes.changeRequests.path);
              }}
            />
          </div>
        ),
      });
    } else {
      items.push(
        createMenuItem({
          key: 'all-change-requests',
          path: `${AppRoutes.changeRequests.path}`,
          icon: iconListCrs,
          label: 'All Change Requests',
          className: 'all-change-requests',
        })
      );
    }
    items.push(
      ...changeRequestState.groupedRecords.map((group: ChangeRequestGroup) => {
        return createItemGroup({
          key: group.key,
          label: group.label,
          items: group.changeRequests,
          getItemProps: (_cr) => {
            const cr = _cr as Result;
            return {
              active: editingThreadId === cr.id,
              label: renderChangeRequestItem(cr),
            };
          },
        });
      })
    );
    return items;
  };

  const mainMenuItems: MenuItem[] = [
    ...(adminAgentEnabled
      ? [
          getItem(
            <Link
              to={AppRoutes.adminAgent.path}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  e.stopPropagation();
                  return;
                }

                e.preventDefault();
                handleNavigation(() => navigate(AppRoutes.adminAgent.path));
              }}
            >
              {AppRoutes.adminAgent.label}
            </Link>,
            AppRoutes.adminAgent.path,
            <Link
              to={AppRoutes.adminAgent.path}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  e.stopPropagation();
                  return;
                }

                e.preventDefault();
                handleNavigation(() => navigate(AppRoutes.adminAgent.path));
              }}
            >
              <AdminAgentChat />
            </Link>,
            'admin-agent-item',
            'app-sidebar-submenu-popup',
            getAdminAgentMenuItems()
          ),
        ]
      : []),
    ...(changeAgentEnabled
      ? [
          getItem(
            <Link
              to={`${AppRoutes.changeRequests.path}/new`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  e.stopPropagation();
                  return;
                }

                e.preventDefault();
                handleNavigation(() =>
                  navigate(`${AppRoutes.changeRequests.path}/new`)
                );
              }}
            >
              {AppRoutes.changeRequests.label}
            </Link>,
            AppRoutes.changeRequests.path,
            <Link
              to={AppRoutes.changeRequests.path}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  e.stopPropagation();
                  return;
                }

                e.preventDefault();
                handleNavigation(() => navigate(AppRoutes.changeRequests.path));
              }}
            >
              <ChangeAgent />
            </Link>,
            'change-agent-item',
            'app-sidebar-submenu-popup',
            getChangeAgentMenuItems()
          ),
        ]
      : []),
    ...(hasAdminAccess
      ? [
          getItem(
            <Link
              to={`${AppRoutes.settings.path}#users`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  e.stopPropagation();
                  return;
                }

                e.preventDefault();
                handleNavigation(() =>
                  navigate(`${AppRoutes.settings.path}#users`)
                );
              }}
            >
              {AppRoutes.settings.label}
            </Link>,
            AppRoutes.settings.path,
            <SettingOutlined />,
            'settings-item'
          ),
        ]
      : []),
  ];

  const onOpenChange = (openKeys: string[], pathName = '') => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1
    );
    // open
    if (currentOpenKey !== undefined) {
      setStateOpenKeys(openKeys);
      const parentItem = pathName || currentOpenKey;
      if (
        parentItem === 'change-agent' &&
        !collapsed &&
        !selectedKeys.includes('new-change-agent')
      ) {
        navigateToNewChangeAgent();
      }
      if (
        !collapsed &&
        !selectedItemKey &&
        parentItem === AppRoutes.adminAgent.path
      ) {
        navigateToNewChat();
      }
    } else {
      // close
      setStateOpenKeys(openKeys);
    }
  };

  return (
    <div id="app-side-menu">
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <Menu
          className={`menu-container items-${hasAdminAccess ? '3' : '2'}`}
          selectedKeys={selectedKeys}
          mode="inline"
          items={mainMenuItems}
          openKeys={stateOpenKeys}
          onOpenChange={onOpenChange}
          onClick={(menuInfo) => {
            agentDispatch(setNewThreadId(''));
            menuInfo.keyPath.length === 1 && onOpenChange([menuInfo.key]);
          }}
        />
      </Sider>
    </div>
  );
};

export default AppSideMenu;
